import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ElementType,
  type ForwardedRef,
  type KeyboardEvent,
  type MouseEvent,
  type MutableRefObject,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
} from "react";

import { cn, hasRenderableNode } from "../base/utils";
import {
  DataExplorerViewTree,
  type DataExplorerViewTreeClassNames,
} from "../data-explorer";
import type {
  NavigationSidebarActiveVariant,
  NavigationSidebarAppearance,
  NavigationSidebarCatalogItem,
  NavigationSidebarCatalogProps,
  NavigationSidebarCatalogSection,
  NavigationSidebarClassNames,
  NavigationSidebarCommonProps,
  NavigationSidebarDisclosureRouteItem,
  NavigationSidebarProps,
  NavigationSidebarRouteChild,
  NavigationSidebarRouteItem,
  NavigationSidebarRouteProps,
  NavigationSidebarStorage,
  NavigationSidebarTreeProps,
} from "./types";
import {
  unifiedIcon,
  unifiedInactive,
  unifiedRow,
  unifiedSelected,
  unifiedStyle,
  unifiedThemeDefaults,
  unifiedTreeClassNames,
} from "./unified-appearance";

interface LayoutBounds {
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  collapsedWidth: number;
  collapseThreshold: number;
}

interface StoredLayout {
  collapsed: boolean;
  width: number;
}

function CollapseIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none">
      <path
        d={
          collapsed
            ? "M4 4.5h12M4 15.5h12M5 7.5 7.5 10 5 12.5M15 7.5 12.5 10l2.5 2.5"
            : "M4 4.5h12M4 15.5h12M7.5 7.5 5 10l2.5 2.5M12.5 7.5 15 10l-2.5 2.5"
        }
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4" fill="none">
      <circle cx="8.5" cy="8.5" r="5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="m12.2 12.2 3.3 3.3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-3.5 w-3.5"
      fill="none"
    >
      <path
        d="m6 6 8 8m0-8-8 8"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="currentColor"
    >
      <circle cx="5" cy="10" r="1.2" />
      <circle cx="10" cy="10" r="1.2" />
      <circle cx="15" cy="10" r="1.2" />
    </svg>
  );
}

function DisclosureIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className={cn(
        "h-4 w-4 transition-transform motion-reduce:transition-none",
        expanded && "rotate-180",
      )}
      fill="none"
    >
      <path
        d="m6 8 4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function assignRef<T>(ref: ForwardedRef<T>, node: T | null) {
  if (typeof ref === "function") {
    ref(node);
  } else if (ref) {
    (ref as MutableRefObject<T | null>).current = node;
  }
}

function requireFiniteNumber(name: string, value: number) {
  if (!Number.isFinite(value)) {
    throw new RangeError(`NavigationSidebar ${name} must be a finite number.`);
  }
}

function resolveLayoutBounds(
  props: NavigationSidebarCommonProps,
): LayoutBounds {
  const minWidth = props.minWidth ?? 180;
  const maxWidth = props.maxWidth ?? 420;
  const defaultWidth = props.defaultWidth ?? 256;
  const collapsedWidth = props.collapsedWidth ?? 60;
  const collapseThreshold = props.collapseThreshold ?? 140;

  requireFiniteNumber("minWidth", minWidth);
  requireFiniteNumber("maxWidth", maxWidth);
  requireFiniteNumber("defaultWidth", defaultWidth);
  requireFiniteNumber("collapsedWidth", collapsedWidth);
  requireFiniteNumber("collapseThreshold", collapseThreshold);

  if (minWidth <= 0)
    throw new RangeError(
      "NavigationSidebar minWidth must be greater than zero.",
    );
  if (maxWidth < minWidth)
    throw new RangeError(
      "NavigationSidebar maxWidth must be greater than or equal to minWidth.",
    );
  if (collapsedWidth < 0)
    throw new RangeError(
      "NavigationSidebar collapsedWidth cannot be negative.",
    );
  if (collapseThreshold < 0)
    throw new RangeError(
      "NavigationSidebar collapseThreshold cannot be negative.",
    );

  return {
    defaultWidth,
    minWidth,
    maxWidth,
    collapsedWidth,
    collapseThreshold,
  };
}

function clampWidth(
  width: number,
  bounds: Pick<LayoutBounds, "minWidth" | "maxWidth">,
) {
  return Math.min(Math.max(width, bounds.minWidth), bounds.maxWidth);
}

function reportStorageError(
  error: unknown,
  onStorageError: ((error: unknown) => void) | undefined,
) {
  if (onStorageError) {
    onStorageError(error);
    return;
  }
  if (typeof console !== "undefined") {
    console.warn("NavigationSidebar could not persist layout state.", error);
  }
}

function resolveStorage(storage: NavigationSidebarStorage | undefined) {
  if (storage) return storage;
  if (typeof window === "undefined") return undefined;
  return window.localStorage;
}

function readStoredLayout(
  props: NavigationSidebarCommonProps,
  bounds: LayoutBounds,
): StoredLayout {
  const fallback = {
    collapsed: props.defaultCollapsed ?? false,
    width: clampWidth(bounds.defaultWidth, bounds),
  };
  if (!props.storageKey) return fallback;

  const storage = resolveStorage(props.storage);
  if (!storage) return fallback;

  try {
    const rawValue = storage.getItem(props.storageKey);
    if (!rawValue) return fallback;
    const parsedValue = JSON.parse(rawValue) as Partial<StoredLayout>;
    return {
      collapsed:
        typeof parsedValue.collapsed === "boolean"
          ? parsedValue.collapsed
          : fallback.collapsed,
      width:
        typeof parsedValue.width === "number" &&
        Number.isFinite(parsedValue.width)
          ? clampWidth(parsedValue.width, bounds)
          : fallback.width,
    };
  } catch (error) {
    reportStorageError(error, props.onStorageError);
    return fallback;
  }
}

function getNodeTitle(label: ReactNode, explicitTitle: string | undefined) {
  if (explicitTitle) return explicitTitle;
  return typeof label === "string" ? label : undefined;
}

function activeVariantClassName(activeVariant: NavigationSidebarActiveVariant) {
  if (activeVariant === "solid")
    return "border-primary bg-primary text-primary-foreground shadow-sm";
  if (activeVariant === "outline")
    return "border-primary/55 bg-background text-foreground ring-1 ring-primary/20";
  if (activeVariant === "muted")
    return "border-transparent bg-muted text-foreground";
  return "border-primary/25 bg-primary/10 text-foreground";
}

function resolveAppearance(
  appearance: NavigationSidebarAppearance | undefined,
) {
  if (appearance?.preset !== undefined && appearance.preset !== "unified") {
    throw new TypeError(
      `Unknown NavigationSidebar preset "${appearance.preset}". Supported preset: unified.`,
    );
  }
  const unified = appearance?.preset === "unified";
  const density = appearance?.density ?? (unified ? "compact" : "default");
  const surface = appearance?.surface ?? "panel";
  const activeVariant =
    appearance?.activeVariant ?? (unified ? "muted" : "soft");
  return {
    unified,
    density,
    surface,
    activeVariant,
    selected: unified ? unifiedSelected : activeVariantClassName(activeVariant),
    inactive: unified
      ? unifiedInactive
      : "text-muted-foreground hover:border-border/70 hover:bg-muted/60 hover:text-foreground",
    root: cn(
      surface === "plain" && "border-transparent bg-transparent shadow-none",
      surface === "panel" &&
        "border-border/70 bg-card text-card-foreground shadow-sm",
      surface === "frosted" &&
        "border-border/55 bg-card/80 text-card-foreground shadow-sm backdrop-blur-xl",
      surface === "outlined" &&
        "border-border bg-background text-foreground shadow-none",
      unified && "rounded-[var(--radius)] shadow-none",
      unified && unifiedThemeDefaults,
      unified &&
        surface === "panel" &&
        "border-[var(--monkeys-component-navigation-sidebar-border,hsl(var(--monkeys-color-border,var(--border))))] bg-[var(--monkeys-component-navigation-sidebar-background,hsl(var(--monkeys-color-card,var(--card))))] text-foreground",
    ),
    itemHeight:
      density === "compact"
        ? unified
          ? "min-h-[var(--monkeys-component-navigation-sidebar-row-height,2.5rem)]"
          : "min-h-9"
        : density === "comfortable"
          ? "min-h-14"
          : "min-h-11",
    itemPadding:
      density === "compact"
        ? unified
          ? "px-2 py-2"
          : "px-2 py-1.5"
        : density === "comfortable"
          ? "px-3 py-3"
          : "px-3 py-2",
    searchHeight:
      density === "compact"
        ? unified
          ? "h-[var(--monkeys-component-navigation-sidebar-row-height,2.5rem)]"
          : "h-8"
        : density === "comfortable"
          ? "h-10"
          : "h-9",
  };
}

const navigationTreeRowHoverClassName =
  "hover:bg-[var(--monkeys-component-navigation-sidebar-tree-row-hover-background,hsl(var(--muted)))] hover:text-[var(--monkeys-component-navigation-sidebar-tree-row-hover-foreground,hsl(var(--foreground)))]";

const navigationTreeRowSelectedClassName =
  "bg-[var(--monkeys-component-navigation-sidebar-tree-row-selected-background,hsl(var(--accent)))] text-[var(--monkeys-component-navigation-sidebar-tree-row-selected-foreground,hsl(var(--accent-foreground)))] hover:bg-[var(--monkeys-component-navigation-sidebar-tree-row-selected-background,hsl(var(--accent)))] hover:text-[var(--monkeys-component-navigation-sidebar-tree-row-selected-foreground,hsl(var(--accent-foreground)))]";

const navigationTreeNestedControlClassName =
  "hover:!bg-transparent hover:!text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

function resolveNavigationTreeClassNames(
  classNames: DataExplorerViewTreeClassNames | undefined,
): DataExplorerViewTreeClassNames {
  return {
    ...classNames,
    groupHeader: cn(navigationTreeRowHoverClassName, classNames?.groupHeader),
    itemSelected: cn(
      navigationTreeRowSelectedClassName,
      classNames?.itemSelected,
    ),
    nodeButton: cn(
      navigationTreeRowHoverClassName,
      "aria-selected:bg-[var(--monkeys-component-navigation-sidebar-tree-row-selected-background,hsl(var(--accent)))] aria-selected:text-[var(--monkeys-component-navigation-sidebar-tree-row-selected-foreground,hsl(var(--accent-foreground)))] aria-selected:hover:bg-[var(--monkeys-component-navigation-sidebar-tree-row-selected-background,hsl(var(--accent)))] aria-selected:hover:text-[var(--monkeys-component-navigation-sidebar-tree-row-selected-foreground,hsl(var(--accent-foreground)))]",
      classNames?.nodeButton,
    ),
    expander: cn(
      navigationTreeNestedControlClassName,
      classNames?.expander,
    ),
    nodeToggle: cn(
      navigationTreeNestedControlClassName,
      classNames?.nodeToggle,
    ),
    groupActions: cn(
      navigationTreeNestedControlClassName,
      classNames?.groupActions,
    ),
    nodeActions: cn(
      navigationTreeNestedControlClassName,
      classNames?.nodeActions,
    ),
    nodeDragHandle: cn(
      navigationTreeNestedControlClassName,
      classNames?.nodeDragHandle,
    ),
  };
}

interface EntryButtonProps {
  item: NavigationSidebarCatalogItem | NavigationSidebarRouteItem;
  active: boolean;
  collapsed: boolean;
  itemAs?: ElementType;
  appearance: ReturnType<typeof resolveAppearance>;
  classNames?: NavigationSidebarClassNames;
  mobile?: boolean;
  embedded?: boolean;
  onSelect?: () => void;
  ariaControls?: string;
  ariaExpanded?: boolean;
  endIndicator?: ReactNode;
}

function entryClassName({
  item,
  active,
  collapsed,
  mobile,
  embedded = false,
  appearance,
  classNames,
  domClassName,
}: {
  item: NavigationSidebarCatalogItem | NavigationSidebarRouteItem;
  active: boolean;
  collapsed: boolean;
  mobile: boolean;
  embedded?: boolean;
  appearance: ReturnType<typeof resolveAppearance>;
  classNames?: NavigationSidebarClassNames;
  domClassName?: string;
}) {
  return cn(
    "group/navigation-item relative flex w-full min-w-0 items-center gap-3 rounded-[var(--radius)] border border-transparent text-left text-sm outline-none transition-[background-color,border-color,color,box-shadow] focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
    appearance.itemHeight,
    appearance.itemPadding,
    appearance.unified && unifiedRow,
    embedded
      ? "min-h-0 self-stretch border-0 bg-transparent text-inherit hover:bg-transparent hover:text-inherit"
      : active
        ? appearance.selected
        : appearance.inactive,
    collapsed && !mobile && "justify-center px-0",
    item.disabled && "pointer-events-none opacity-50",
    classNames?.item,
    active && classNames?.itemActive,
    mobile && classNames?.mobileItem,
    domClassName,
    item.className,
  );
}

function EntryContent({
  item,
  collapsed,
  mobile,
  classNames,
  endIndicator,
  appearance,
}: {
  item: NavigationSidebarCatalogItem | NavigationSidebarRouteItem;
  collapsed: boolean;
  mobile: boolean;
  classNames?: NavigationSidebarClassNames;
  endIndicator?: ReactNode;
  appearance: ReturnType<typeof resolveAppearance>;
}) {
  return (
    <>
      {hasRenderableNode(item.icon) ? (
        <span
          className={cn(
            "flex size-5 shrink-0 items-center justify-center [&_svg]:size-full",
            appearance.unified && unifiedIcon,
            classNames?.itemIcon,
          )}
          aria-hidden="true"
        >
          {item.icon}
        </span>
      ) : null}
      {mobile || !collapsed ? (
        <span
          className={cn(
            "flex min-w-0 flex-1 flex-col",
            classNames?.itemContent,
          )}
        >
          <span
            className={cn(
              "min-w-0 truncate font-semibold",
              appearance.unified && "font-medium",
              classNames?.itemLabel,
            )}
          >
            {item.label}
          </span>
          {hasRenderableNode(item.description) ? (
            <span
              className={cn(
                "mt-0.5 truncate text-xs font-normal",
                appearance.unified && "text-inherit",
                classNames?.itemDescription,
              )}
            >
              {item.description}
            </span>
          ) : null}
        </span>
      ) : null}
      {(mobile || !collapsed) && hasRenderableNode(item.meta) ? (
        <span
          className={cn(
            "shrink-0 text-xs text-muted-foreground",
            classNames?.itemMeta,
          )}
        >
          {item.meta}
        </span>
      ) : null}
      {(mobile || !collapsed) && hasRenderableNode(item.endContent) ? (
        <span className={cn("shrink-0", classNames?.itemEndContent)}>
          {item.endContent}
        </span>
      ) : null}
      {(mobile || !collapsed) && hasRenderableNode(endIndicator) ? (
        <span className="flex shrink-0 items-center justify-center">
          {endIndicator}
        </span>
      ) : null}
    </>
  );
}

function EntryButton({
  item,
  active,
  collapsed,
  itemAs,
  appearance,
  classNames,
  mobile = false,
  embedded = false,
  onSelect,
  ariaControls,
  ariaExpanded,
  endIndicator,
}: EntryButtonProps) {
  const ItemComponent = itemAs ?? (item.href ? "a" : "button");
  const title = getNodeTitle(item.label, item.title);
  const domProps = item.domProps ?? {};
  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    item.onClick?.(event);
    onSelect?.();
  };
  const componentProps = {
    ...domProps,
    ...(item.to ? { to: item.to } : {}),
    ...(item.href ? { href: item.href } : {}),
    ...(ItemComponent === "button"
      ? { type: "button", disabled: item.disabled }
      : {}),
    title,
    "aria-current": active ? "page" : undefined,
    "aria-disabled": item.disabled || undefined,
    "aria-controls": ariaControls,
    "aria-expanded": ariaExpanded,
    tabIndex: item.disabled ? -1 : domProps.tabIndex,
    onClick: handleClick,
  };

  return (
    <ItemComponent
      {...componentProps}
      data-navigation-sidebar-item-id={item.id}
      data-active={active ? "true" : "false"}
      data-expanded={ariaExpanded === undefined ? undefined : String(ariaExpanded)}
      className={entryClassName({
        item,
        active,
        collapsed,
        mobile,
        embedded,
        appearance,
        classNames,
        domClassName: domProps.className,
      })}
    >
      <EntryContent
        appearance={appearance}
        item={item}
        collapsed={collapsed}
        mobile={mobile}
        classNames={classNames}
        endIndicator={endIndicator}
      />
    </ItemComponent>
  );
}

function EntryPresentation({
  item,
  appearance,
  classNames,
}: {
  item: NavigationSidebarRouteItem;
  appearance: ReturnType<typeof resolveAppearance>;
  classNames?: NavigationSidebarClassNames;
}) {
  const domProps = item.domProps ?? {};
  return (
    <div
      {...domProps}
      data-navigation-sidebar-item-id={item.id}
      aria-disabled={item.disabled || undefined}
      title={getNodeTitle(item.label, item.title)}
      className={entryClassName({
        item,
        active: false,
        collapsed: false,
        mobile: true,
        embedded: true,
        appearance,
        classNames,
        domClassName: domProps.className,
      })}
    >
      <EntryContent
        appearance={appearance}
        item={item}
        collapsed={false}
        mobile
        classNames={classNames}
      />
    </div>
  );
}

function routeRowClassName({
  active,
  appearance,
}: {
  active: boolean;
  appearance: ReturnType<typeof resolveAppearance>;
}) {
  return cn(
    "flex w-full min-w-0 items-center gap-0.5 rounded-[var(--radius)] border border-transparent pr-1 text-sm transition-[background-color,border-color,color,box-shadow] motion-reduce:transition-none",
    appearance.itemHeight,
    appearance.unified && unifiedRow,
    active ? appearance.selected : appearance.inactive,
  );
}

function filterCatalogItems(
  items: NavigationSidebarCatalogItem[],
  activeFilterId: string | undefined,
) {
  if (!activeFilterId) return items;
  return items.filter(
    (item) =>
      !item.filterIds?.length || item.filterIds.includes(activeFilterId),
  );
}

function CatalogContent({
  props,
  collapsed,
  appearance,
}: {
  props: NavigationSidebarCatalogProps;
  collapsed: boolean;
  appearance: ReturnType<typeof resolveAppearance>;
}) {
  const {
    items = [],
    sections = [],
    filters = [],
    activeFilterId,
    activeItemId,
    onFilterSelect,
    itemAs,
    layout = "single",
    empty,
    ariaLabel,
    classNames,
  } = props;
  const filteredItems = filterCatalogItems(items, activeFilterId);
  const filteredSections = sections
    .map((section) => ({
      ...section,
      items: filterCatalogItems(section.items, activeFilterId),
    }))
    .filter((section) => section.items.length > 0);
  const hasItems = filteredItems.length > 0 || filteredSections.length > 0;

  const filtersNode =
    filters.length > 0 && !collapsed ? (
      <div
        role="group"
        className={cn(
          layout === "split"
            ? "flex min-w-32 flex-col gap-1 border-r border-border/60 pr-2"
            : "flex flex-wrap gap-1",
          classNames?.filterList,
        )}
        aria-label={ariaLabel ? `${ariaLabel} filters` : undefined}
      >
        {filters.map((filter) => {
          const active = filter.id === activeFilterId;
          return (
            <button
              key={filter.id}
              type="button"
              title={
                filter.title ??
                (typeof filter.label === "string" ? filter.label : undefined)
              }
              aria-pressed={active}
              disabled={filter.disabled}
              className={cn(
                "inline-flex min-h-8 items-center gap-2 rounded-md px-2.5 text-xs font-medium text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
                active && "bg-muted text-foreground",
                classNames?.filterItem,
                active && classNames?.filterItemActive,
              )}
              onClick={() => onFilterSelect?.(filter)}
            >
              {hasRenderableNode(filter.icon) ? (
                <span aria-hidden="true">{filter.icon}</span>
              ) : null}
              <span className="truncate">{filter.label}</span>
            </button>
          );
        })}
      </div>
    ) : null;

  const listNode = hasItems ? (
    <div className={cn("min-w-0 flex-1", classNames?.list)}>
      {filteredItems.length > 0 ? (
        <ul className="grid list-none gap-1 p-0">
          {filteredItems.map((item) => (
            <li key={item.id}>
              <EntryButton
                item={item}
                active={item.active ?? item.id === activeItemId}
                collapsed={collapsed}
                itemAs={itemAs}
                appearance={appearance}
                classNames={classNames}
              />
            </li>
          ))}
        </ul>
      ) : null}
      {filteredSections.map((section) => (
        <section
          key={section.id}
          className={cn(
            "mt-3 first:mt-0",
            classNames?.section,
            section.className,
          )}
        >
          {!collapsed && hasRenderableNode(section.label) ? (
            <div
              className={cn(
                "mb-1 px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground",
                classNames?.sectionLabel,
                section.labelClassName,
              )}
            >
              {section.label}
            </div>
          ) : null}
          <ul className="grid list-none gap-1 p-0">
            {section.items.map((item) => (
              <li key={item.id}>
                <EntryButton
                  item={item}
                  active={item.active ?? item.id === activeItemId}
                  collapsed={collapsed}
                  itemAs={itemAs}
                  appearance={appearance}
                  classNames={classNames}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  ) : (
    <div className="px-3 py-8 text-center text-sm text-muted-foreground">
      {empty}
    </div>
  );

  return (
    <nav
      aria-label={ariaLabel}
      className={cn(layout === "split" && !collapsed && "flex min-w-0 gap-2")}
    >
      {filtersNode}
      {listNode}
    </nav>
  );
}

function ChildEntry({
  child,
  parent,
  active,
  interactive,
  onSelect,
  appearance,
  classNames,
  embedded = false,
}: {
  child: NavigationSidebarRouteChild;
  parent: NavigationSidebarRouteItem;
  active: boolean;
  interactive: boolean;
  onSelect?: (
    child: NavigationSidebarRouteChild,
    parent: NavigationSidebarRouteItem,
  ) => void;
  appearance: ReturnType<typeof resolveAppearance>;
  classNames?: NavigationSidebarClassNames;
  embedded?: boolean;
}) {
  const Component = interactive ? "button" : "div";
  const domProps = child.domProps ?? {};
  return (
    <Component
      {...domProps}
      {...(interactive ? { type: "button" } : {})}
      data-navigation-sidebar-child-id={child.id}
      aria-current={interactive && active ? "page" : undefined}
      aria-disabled={child.disabled || undefined}
      title={getNodeTitle(child.label, child.title)}
      disabled={interactive ? child.disabled : undefined}
      className={cn(
        "flex w-full min-w-0 flex-1 items-center gap-3 rounded-[var(--radius)] border border-transparent text-left text-sm font-semibold outline-none transition-[background-color,border-color,color,box-shadow] focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
        appearance.itemHeight,
        appearance.itemPadding,
        appearance.unified && unifiedRow,
        embedded
          ? "min-h-0 self-stretch border-0 bg-transparent text-inherit hover:bg-transparent hover:text-inherit"
          : active
            ? appearance.selected
            : appearance.inactive,
        child.disabled && "pointer-events-none opacity-50",
        classNames?.childItem,
        active && classNames?.childItemActive,
        domProps.className,
        child.className,
      )}
      onClick={
        interactive && !child.disabled
          ? () => onSelect?.(child, parent)
          : undefined
      }
    >
      {hasRenderableNode(child.icon) ? (
        <span
          className={cn(
            "flex size-5 shrink-0 items-center justify-center [&_svg]:size-full",
            appearance.unified && unifiedIcon,
          )}
          aria-hidden="true"
        >
          {child.icon}
        </span>
      ) : null}
      <span className="min-w-0 truncate">{child.label}</span>
    </Component>
  );
}

function RouteActions({
  itemId,
  actions,
  kind,
  className,
  mobile = false,
}: {
  itemId: string;
  actions: ReactNode;
  kind: "item" | "child";
  className?: string;
  mobile?: boolean;
}) {
  if (!hasRenderableNode(actions)) return null;
  return (
    <div
      data-navigation-sidebar-item-actions={
        kind === "item" ? itemId : undefined
      }
      data-navigation-sidebar-child-actions={
        kind === "child" ? itemId : undefined
      }
      data-navigation-sidebar-actions-mobile={mobile ? "true" : undefined}
      className={cn(
        "ml-auto flex shrink-0 items-center gap-1 [&>button]:border-0 [&>button]:bg-transparent [&>button]:shadow-none [&>button:hover]:bg-muted",
        className,
      )}
    >
      {actions}
    </div>
  );
}

function isDisclosureRouteItem(
  item: NavigationSidebarRouteItem,
): item is NavigationSidebarDisclosureRouteItem {
  return item.interaction === "disclosure";
}

function validateRouteItems(routeItems: NavigationSidebarRouteItem[]) {
  const itemIds = new Set<string>();
  const childIds = new Set<string>();
  const collapsibleItemIds = new Set<string>();

  for (const item of routeItems) {
    const itemId = item.id;
    if (itemIds.has(itemId)) {
      throw new RangeError(
        `NavigationSidebar route item ids must be unique; received "${itemId}" more than once.`,
      );
    }
    itemIds.add(itemId);

    const disclosure = isDisclosureRouteItem(item);
    if (disclosure) {
      if (!item.children.length) {
        throw new RangeError(
          `NavigationSidebar disclosure item "${item.id}" must include at least one child.`,
        );
      }
      if (
        item.href !== undefined ||
        item.to !== undefined ||
        item.onClick !== undefined
      ) {
        throw new TypeError(
          `NavigationSidebar disclosure item "${itemId}" cannot define href, to, or onClick.`,
        );
      }
      collapsibleItemIds.add(item.id);
    } else if (item.childrenPresentation === "collapsible") {
      if (!item.children?.length) {
        throw new RangeError(
          `NavigationSidebar collapsible route item "${item.id}" must include at least one child.`,
        );
      }
      collapsibleItemIds.add(item.id);
    }

    for (const child of item.children ?? []) {
      if (childIds.has(child.id)) {
        throw new RangeError(
          `NavigationSidebar route child ids must be unique; received "${child.id}" more than once.`,
        );
      }
      childIds.add(child.id);
    }
  }

  return collapsibleItemIds;
}

function RouteChildrenToggle({
  item,
  expanded,
  controls,
  label,
  onToggle,
  classNames,
}: {
  item: NavigationSidebarRouteItem;
  expanded: boolean;
  controls: string;
  label: string;
  onToggle: () => void;
  classNames?: NavigationSidebarClassNames;
}) {
  const itemLabel = typeof item.label === "string" ? item.label : item.id;
  return (
    <button
      type="button"
      disabled={item.disabled}
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={`${label}: ${itemLabel}`}
      title={label}
      data-navigation-sidebar-disclosure-trigger={item.id}
      className={cn(
        "inline-flex h-8 w-7 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground shadow-none outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
        classNames?.childMenuTrigger,
      )}
      onClick={onToggle}
    >
      <DisclosureIcon expanded={expanded} />
    </button>
  );
}

function CollapsedChildMenu({
  item,
  activeChildId,
  label,
  onChildSelect,
  classNames,
  appearance,
  triggerVariant = "submenu",
  mobile = false,
}: {
  item: NavigationSidebarRouteItem;
  activeChildId?: string;
  label: string;
  onChildSelect?: (
    child: NavigationSidebarRouteChild,
    parent: NavigationSidebarRouteItem,
  ) => void;
  classNames?: NavigationSidebarClassNames;
  appearance?: ReturnType<typeof resolveAppearance>;
  triggerVariant?: "submenu" | "item" | "disclosure";
  mobile?: boolean;
}) {
  const enabledChildren = (item.children ?? []).filter(
    (child) => !child.disabled,
  );
  if (!item.children?.length) return null;
  if (
    triggerVariant === "submenu" &&
    (!onChildSelect || enabledChildren.length === 0)
  )
    return null;
  const itemLabel = typeof item.label === "string" ? item.label : item.id;
  const itemTriggerDisabled =
    Boolean(item.disabled) || !onChildSelect || enabledChildren.length === 0;
  const domProps = item.domProps ?? {};
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        {triggerVariant === "item" && appearance ? (
          <button
            {...domProps}
            type="button"
            disabled={itemTriggerDisabled}
            aria-label={`${label}: ${itemLabel}`}
            title={label}
            data-navigation-sidebar-item-id={item.id}
            data-navigation-sidebar-disclosure-menu="true"
            className={entryClassName({
              item,
              active: false,
              collapsed: !mobile,
              mobile,
              appearance,
              classNames,
              domClassName: domProps.className,
            })}
          >
            <EntryContent
              appearance={appearance}
              item={item}
              collapsed={!mobile}
              mobile={mobile}
              classNames={classNames}
              endIndicator={mobile ? <DisclosureIcon expanded={false} /> : undefined}
            />
          </button>
        ) : (
          <button
            type="button"
            disabled={
              triggerVariant === "disclosure" ? itemTriggerDisabled : undefined
            }
            data-navigation-sidebar-disclosure-trigger={
              triggerVariant === "disclosure" ? item.id : undefined
            }
            className={cn(
              "inline-flex h-8 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground shadow-none outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
              triggerVariant === "disclosure" ? "w-7" : "w-6",
              classNames?.childMenuTrigger,
            )}
            aria-label={`${label}: ${itemLabel}`}
            title={label}
          >
            {triggerVariant === "disclosure" ? (
              <DisclosureIcon expanded={false} />
            ) : (
              <MoreIcon />
            )}
          </button>
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="right"
          align="start"
          sideOffset={8}
          className={cn(
            "z-50 min-w-40 rounded-md border border-border bg-popover p-1 text-popover-foreground shadow-md outline-none",
            classNames?.childMenuContent,
          )}
        >
          {item.children?.map((child) => (
            <DropdownMenu.Item
              key={child.id}
              disabled={child.disabled}
              data-navigation-sidebar-child-id={child.id}
              data-active={child.id === activeChildId ? "true" : "false"}
              className={cn(
                "flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                child.id === activeChildId &&
                  "bg-accent text-accent-foreground",
              )}
              onSelect={() => onChildSelect?.(child, item)}
            >
              {hasRenderableNode(child.icon) ? (
                <span aria-hidden="true">{child.icon}</span>
              ) : null}
              <span className="truncate">{child.label}</span>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function RouteContent({
  props,
  collapsed,
  appearance,
}: {
  props: NavigationSidebarRouteProps;
  collapsed: boolean;
  appearance: ReturnType<typeof resolveAppearance>;
}) {
  const {
    routeItems,
    activeItemId,
    activeChildId,
    expandedItemIds,
    defaultExpandedItemIds = [],
    itemAs,
    onItemSelect,
    onChildSelect,
    onExpandedItemIdsChange,
    collapsedChildMenuLabel = "Open submenu",
    disclosureLabel = "Toggle submenu",
    ariaLabel,
    classNames,
  } = props;
  const disclosureId = useId();
  const collapsibleItemIds = useMemo(
    () => validateRouteItems(routeItems),
    [routeItems],
  );
  const [internalExpandedItemIds, setInternalExpandedItemIds] = useState(
    () => defaultExpandedItemIds,
  );
  const isExpandedControlled = expandedItemIds !== undefined;
  const resolvedExpandedItemIds =
    expandedItemIds ?? internalExpandedItemIds;
  const normalizedExpandedItemIds = useMemo(
    () =>
      resolvedExpandedItemIds.filter((itemId) =>
        collapsibleItemIds.has(itemId),
      ),
    [collapsibleItemIds, resolvedExpandedItemIds],
  );
  const expandedItemIdSet = useMemo(
    () => new Set(normalizedExpandedItemIds),
    [normalizedExpandedItemIds],
  );
  const toggleItemExpanded = useCallback(
    (item: NavigationSidebarRouteItem) => {
      if (item.disabled || !collapsibleItemIds.has(item.id)) return;
      const nextExpandedItemIds = expandedItemIdSet.has(item.id)
        ? normalizedExpandedItemIds.filter((itemId) => itemId !== item.id)
        : [...normalizedExpandedItemIds, item.id];
      if (!isExpandedControlled) {
        setInternalExpandedItemIds(nextExpandedItemIds);
      }
      onExpandedItemIdsChange?.(nextExpandedItemIds);
    },
    [
      collapsibleItemIds,
      expandedItemIdSet,
      isExpandedControlled,
      normalizedExpandedItemIds,
      onExpandedItemIdsChange,
    ],
  );
  return (
    <nav aria-label={ariaLabel}>
      <ul
        className={cn(
          "grid list-none gap-2 p-0",
          appearance.unified && "gap-0.5",
          classNames?.list,
        )}
      >
        {routeItems.map((item, itemIndex) => {
          const disclosure = isDisclosureRouteItem(item);
          const active = !disclosure && item.id === activeItemId;
          const hasChildren = Boolean(item.children?.length);
          const childrenCollapsible = collapsibleItemIds.has(item.id);
          const childrenExpanded =
            !childrenCollapsible || expandedItemIdSet.has(item.id);
          const showItemActions =
            !collapsed && hasRenderableNode(item.actions);
          const compoundItemRow =
            (collapsed && hasChildren && Boolean(onChildSelect)) ||
            (!collapsed && childrenCollapsible && !disclosure) ||
            showItemActions;
          const childListId = `${disclosureId}-route-children-${itemIndex}`;
          return (
            <li key={item.id} className="grid min-w-0 gap-1">
              {collapsed && disclosure ? (
                <CollapsedChildMenu
                  item={item}
                  activeChildId={activeChildId}
                  label={collapsedChildMenuLabel}
                  onChildSelect={onChildSelect}
                  classNames={classNames}
                  appearance={appearance}
                  triggerVariant="item"
                />
              ) : (
                <div
                  data-navigation-sidebar-item-row={
                    compoundItemRow ? item.id : undefined
                  }
                  data-active={compoundItemRow ? String(active) : undefined}
                  className={
                    compoundItemRow
                      ? routeRowClassName({ active, appearance })
                      : undefined
                  }
                >
                  <EntryButton
                    item={item}
                    active={active}
                    collapsed={collapsed}
                    embedded={compoundItemRow}
                    itemAs={disclosure ? "button" : itemAs}
                    appearance={appearance}
                    classNames={classNames}
                    onSelect={
                      disclosure
                        ? () => toggleItemExpanded(item)
                        : onItemSelect
                          ? () => onItemSelect(item)
                          : undefined
                    }
                    ariaControls={
                      !collapsed && childrenCollapsible && disclosure
                        ? childListId
                        : undefined
                    }
                    ariaExpanded={
                      !collapsed && childrenCollapsible && disclosure
                        ? childrenExpanded
                        : undefined
                    }
                    endIndicator={
                      !collapsed && disclosure && !showItemActions ? (
                        <DisclosureIcon expanded={childrenExpanded} />
                      ) : undefined
                    }
                  />
                  {showItemActions ? (
                    <RouteActions
                      itemId={item.id}
                      actions={item.actions}
                      kind="item"
                      className={classNames?.itemActions}
                    />
                  ) : null}
                  {collapsed && onChildSelect ? (
                    <CollapsedChildMenu
                      item={item}
                      activeChildId={activeChildId}
                      label={collapsedChildMenuLabel}
                      onChildSelect={onChildSelect}
                      classNames={classNames}
                    />
                  ) : null}
                  {!collapsed &&
                  childrenCollapsible &&
                  (!disclosure || showItemActions) ? (
                    <RouteChildrenToggle
                      item={item}
                      expanded={childrenExpanded}
                      controls={childListId}
                      label={disclosureLabel}
                      onToggle={() => toggleItemExpanded(item)}
                      classNames={classNames}
                    />
                  ) : null}
                </div>
              )}
              {!collapsed && hasChildren ? (
                <ul
                  id={childListId}
                  hidden={!childrenExpanded}
                  style={childrenExpanded ? undefined : { display: "none" }}
                  className={cn(
                    "ml-5 grid list-none gap-0.5 border-l border-border/70 py-1 pl-3",
                    appearance.unified && "border-border",
                    classNames?.childList,
                  )}
                >
                  {item.children?.map((child) => {
                    const hasChildActions = hasRenderableNode(child.actions);
                    const childActive = child.id === activeChildId;
                    const childEntry = (
                      <ChildEntry
                        child={child}
                        parent={item}
                        active={childActive}
                        interactive={Boolean(onChildSelect)}
                        onSelect={onChildSelect}
                        appearance={appearance}
                        classNames={classNames}
                        embedded={hasChildActions}
                      />
                    );
                    return (
                      <li key={child.id}>
                        {hasChildActions ? (
                          <div
                            data-navigation-sidebar-child-row={child.id}
                            data-active={String(childActive)}
                            className={routeRowClassName({
                              active: childActive,
                              appearance,
                            })}
                          >
                            {childEntry}
                            <RouteActions
                              itemId={child.id}
                              actions={child.actions}
                              kind="child"
                              className={classNames?.childActions}
                            />
                          </div>
                        ) : (
                          childEntry
                        )}
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function flattenCatalogItems(
  items: NavigationSidebarCatalogItem[],
  sections: NavigationSidebarCatalogSection[],
  activeFilterId: string | undefined,
) {
  return [
    ...filterCatalogItems(items, activeFilterId),
    ...sections.flatMap((section) =>
      filterCatalogItems(section.items, activeFilterId),
    ),
  ];
}

function MobileNavigation({
  props,
  appearance,
}: {
  props: NavigationSidebarCatalogProps | NavigationSidebarRouteProps;
  appearance: ReturnType<typeof resolveAppearance>;
}) {
  if (!props.mobile || props.mobile.mode !== "horizontal") return null;
  const classNames = props.classNames;
  const items =
    props.kind === "catalog"
      ? flattenCatalogItems(
          props.items ?? [],
          props.sections ?? [],
          props.activeFilterId,
        )
      : props.routeItems;
  return (
    <nav
      aria-label={props.ariaLabel}
      className={cn(
        "flex gap-2 overflow-x-auto pb-1",
        classNames?.mobileNav,
        props.mobile.className,
      )}
    >
      {items.map((item) => {
        const mobileClassNames = {
          ...classNames,
          mobileItem: cn(
            classNames?.mobileItem,
            props.mobile?.itemClassName,
          ),
        };
        const routeItem =
          props.kind === "route"
            ? (item as NavigationSidebarRouteItem)
            : undefined;
        const showRouteActions =
          routeItem !== undefined && hasRenderableNode(routeItem.actions);
        if (props.kind === "route" && isDisclosureRouteItem(item)) {
          return showRouteActions ? (
            <div
              key={item.id}
              data-navigation-sidebar-item-row={item.id}
              data-active="false"
              className={cn(
                routeRowClassName({ active: false, appearance }),
                "shrink-0",
              )}
            >
              <EntryPresentation
                item={item}
                appearance={appearance}
                classNames={mobileClassNames}
              />
              <RouteActions
                itemId={item.id}
                actions={item.actions}
                kind="item"
                className={classNames?.itemActions}
                mobile
              />
              <CollapsedChildMenu
                item={item}
                activeChildId={props.activeChildId}
                label={props.collapsedChildMenuLabel ?? "Open submenu"}
                onChildSelect={props.onChildSelect}
                classNames={mobileClassNames}
                appearance={appearance}
                triggerVariant="disclosure"
                mobile
              />
            </div>
          ) : (
            <Fragment key={item.id}>
              <CollapsedChildMenu
                item={item}
                activeChildId={props.activeChildId}
                label={props.collapsedChildMenuLabel ?? "Open submenu"}
                onChildSelect={props.onChildSelect}
                classNames={mobileClassNames}
                appearance={appearance}
                triggerVariant="item"
                mobile
              />
            </Fragment>
          );
        }
        const active =
          props.kind === "catalog"
            ? ((item as NavigationSidebarCatalogItem).active ??
              item.id === props.activeItemId)
            : item.id === props.activeItemId;
        const entryButton = (
          <EntryButton
            item={item}
            active={active}
            collapsed={false}
            embedded={showRouteActions}
            itemAs={props.itemAs}
            appearance={appearance}
            classNames={mobileClassNames}
            mobile
            onSelect={
              props.kind === "route" && props.onItemSelect
                ? () =>
                    props.onItemSelect?.(item as NavigationSidebarRouteItem)
                : undefined
            }
          />
        );
        return showRouteActions && routeItem ? (
          <div
            key={item.id}
            data-navigation-sidebar-item-row={item.id}
            data-active={String(active)}
            className={cn(
              routeRowClassName({ active, appearance }),
              "shrink-0",
            )}
          >
            {entryButton}
            <RouteActions
              itemId={routeItem.id}
              actions={routeItem.actions}
              kind="item"
              className={classNames?.itemActions}
              mobile
            />
          </div>
        ) : (
          <Fragment key={item.id}>{entryButton}</Fragment>
        );
      })}
    </nav>
  );
}

function NavigationSidebarInner<
  TNodeSource = unknown,
  TGroupSource = unknown,
  TContext = unknown,
>(
  props: NavigationSidebarProps<TNodeSource, TGroupSource, TContext>,
  ref: ForwardedRef<HTMLElement>,
) {
  const bounds = resolveLayoutBounds(props);
  if (props.width !== undefined) requireFiniteNumber("width", props.width);
  const initialLayoutRef = useRef<StoredLayout | null>(null);
  if (!initialLayoutRef.current) {
    initialLayoutRef.current = readStoredLayout(props, bounds);
  }

  const sidebarRef = useRef<HTMLElement | null>(null);
  const [internalCollapsed, setInternalCollapsed] = useState(
    initialLayoutRef.current.collapsed,
  );
  const [internalWidth, setInternalWidth] = useState(
    initialLayoutRef.current.width,
  );
  const [internalSearchValue, setInternalSearchValue] = useState(
    props.search?.defaultValue ?? "",
  );
  const [isResizing, setIsResizing] = useState(false);
  const resolvedCollapsed = props.collapsed ?? internalCollapsed;
  const resolvedWidth = clampWidth(props.width ?? internalWidth, bounds);
  const resolvedSearchValue = props.search?.value ?? internalSearchValue;
  const isCollapsedControlled = props.collapsed !== undefined;
  const isWidthControlled = props.width !== undefined;
  const appearance = useMemo(
    () => resolveAppearance(props.appearance),
    [props.appearance],
  );
  const collapsible = props.collapsible ?? true;
  const resizable = props.resizable ?? true;
  const collapsePlacement = collapsible
    ? (props.collapsePlacement ?? "header")
    : "none";
  const collapseLabel = props.collapseLabel ?? "Collapse sidebar";
  const expandLabel = props.expandLabel ?? "Expand sidebar";
  const resizeLabel = props.resizeLabel ?? "Resize sidebar";
  const contentVisible = !resolvedCollapsed || bounds.collapsedWidth > 0;

  const setSidebarRef = (node: HTMLElement | null) => {
    sidebarRef.current = node;
    assignRef(ref, node);
  };

  const updateCollapsed = useCallback(
    (nextCollapsed: boolean) => {
      if (!collapsible && nextCollapsed) return;
      if (!isCollapsedControlled) setInternalCollapsed(nextCollapsed);
      props.onCollapsedChange?.(nextCollapsed);
    },
    [collapsible, isCollapsedControlled, props.onCollapsedChange],
  );

  const updateWidth = useCallback(
    (nextWidth: number) => {
      const clampedWidth = clampWidth(nextWidth, bounds);
      if (!isWidthControlled) setInternalWidth(clampedWidth);
      props.onWidthChange?.(clampedWidth);
    },
    [bounds.maxWidth, bounds.minWidth, isWidthControlled, props.onWidthChange],
  );

  useEffect(() => {
    if (!props.storageKey) return;
    const storage = resolveStorage(props.storage);
    if (!storage) return;
    try {
      storage.setItem(
        props.storageKey,
        JSON.stringify({
          collapsed: resolvedCollapsed,
          width: resolvedWidth,
        }),
      );
    } catch (error) {
      reportStorageError(error, props.onStorageError);
    }
  }, [
    props.onStorageError,
    props.storage,
    props.storageKey,
    resolvedCollapsed,
    resolvedWidth,
  ]);

  useEffect(() => {
    if (!isResizing) return undefined;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const handlePointerMove = (event: globalThis.PointerEvent) => {
      const sidebar = sidebarRef.current;
      if (!sidebar) return;
      const nextWidth = event.clientX - sidebar.getBoundingClientRect().left;
      if (collapsible && nextWidth < bounds.collapseThreshold) {
        updateCollapsed(true);
        return;
      }
      updateCollapsed(false);
      updateWidth(nextWidth);
    };
    const handlePointerUp = () => setIsResizing(false);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [
    bounds.collapseThreshold,
    collapsible,
    isResizing,
    updateCollapsed,
    updateWidth,
  ]);

  const handleResizeKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    if (event.key === "ArrowLeft") {
      const nextWidth = resolvedWidth - 24;
      if (collapsible && nextWidth < bounds.collapseThreshold) {
        updateCollapsed(true);
      } else {
        updateWidth(nextWidth);
      }
      return;
    }
    if (resolvedCollapsed) updateCollapsed(false);
    updateWidth(Math.max(resolvedWidth + 24, bounds.minWidth));
  };

  const collapseButton = (extraClassName?: string) => (
    <button
      type="button"
      className={cn(
        "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none",
        props.classNames?.collapseButton,
        extraClassName,
      )}
      aria-label={resolvedCollapsed ? expandLabel : collapseLabel}
      title={resolvedCollapsed ? expandLabel : collapseLabel}
      aria-expanded={!resolvedCollapsed}
      onClick={() => updateCollapsed(!resolvedCollapsed)}
    >
      <CollapseIcon collapsed={resolvedCollapsed} />
    </button>
  );

  const hasHeader =
    hasRenderableNode(props.title) ||
    hasRenderableNode(props.headerLeading) ||
    hasRenderableNode(props.headerActions) ||
    collapsePlacement === "header";

  const renderContent = () => {
    if (props.kind === "catalog") {
      return (
        <CatalogContent
          props={props}
          collapsed={resolvedCollapsed}
          appearance={appearance}
        />
      );
    }
    if (props.kind === "route") {
      return (
        <RouteContent
          props={props}
          collapsed={resolvedCollapsed}
          appearance={appearance}
        />
      );
    }
    const treeProps = (
      props as NavigationSidebarTreeProps<TNodeSource, TGroupSource, TContext>
    ).tree;
    return (
      <DataExplorerViewTree
        {...treeProps}
        classNames={
          appearance.unified
            ? unifiedTreeClassNames(
                appearance.itemHeight,
                appearance.itemPadding,
                treeProps.classNames,
              )
            : resolveNavigationTreeClassNames(treeProps.classNames)
        }
      />
    );
  };

  return (
    <>
      <aside
        {...props.rootDomProps}
        ref={setSidebarRef}
        aria-label={props.ariaLabel}
        data-monkeys-component={props.dataComponent ?? "navigation-sidebar"}
        data-navigation-sidebar-kind={props.kind}
        data-navigation-sidebar-preset={props.appearance?.preset}
        data-collapsed={resolvedCollapsed ? "true" : "false"}
        className={cn(
          "relative flex h-full min-h-0 shrink-0 flex-col overflow-visible rounded-[calc(var(--radius)+0.25rem)] border transition-[width,opacity] duration-300 motion-reduce:transition-none",
          appearance.root,
          props.desktopClassName,
          props.classNames?.root,
          props.rootDomProps?.className,
          props.className,
        )}
        style={{
          ...(appearance.unified ? unifiedStyle(props.appearance?.activeVariant) : {}),
          ...props.style,
          width:
            props.widthMode === "fill"
              ? undefined
              : resolvedCollapsed
                ? bounds.collapsedWidth
                : resolvedWidth,
          transition: isResizing ? "none" : props.style?.transition,
        }}
      >
        {resizable ? (
          <button
            type="button"
            role="separator"
            aria-orientation="vertical"
            aria-valuemin={bounds.minWidth}
            aria-valuemax={bounds.maxWidth}
            aria-valuenow={resolvedWidth}
            aria-label={resizeLabel}
            title={resizeLabel}
            className={cn(
              "group/resize absolute -right-1.5 bottom-0 top-0 z-40 w-3 cursor-col-resize bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-ring",
              props.classNames?.resizeHandle,
            )}
            onPointerDown={(event: PointerEvent<HTMLButtonElement>) => {
              event.preventDefault();
              setIsResizing(true);
            }}
            onKeyDown={handleResizeKeyDown}
          >
            <span className="pointer-events-none absolute bottom-3 right-1/2 top-3 w-px translate-x-1/2 rounded-full bg-border opacity-0 transition-opacity group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100" />
          </button>
        ) : null}

        {collapsePlacement === "edge"
          ? collapseButton(
              "absolute -right-3 top-1/2 z-50 -translate-y-1/2 border border-border bg-background shadow-sm",
            )
          : null}

        {contentVisible ? (
          <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden">
            {hasHeader ? (
              <div
                className={cn(
                  "flex min-h-12 min-w-0 items-center gap-2 border-b border-border/60 px-3",
                  appearance.unified &&
                    "min-h-14 border-border px-[var(--monkeys-component-navigation-sidebar-content-inline-padding,var(--global-spacing,0.75rem))]",
                  resolvedCollapsed && "justify-center px-1",
                  props.classNames?.header,
                )}
              >
                {hasRenderableNode(props.headerLeading) ? (
                  <span
                    className={cn(
                      "flex shrink-0 items-center justify-center",
                      props.classNames?.headerLeading,
                    )}
                  >
                    {props.headerLeading}
                  </span>
                ) : null}
                {!resolvedCollapsed && hasRenderableNode(props.title) ? (
                  <div
                    className={cn(
                      "min-w-0 flex-1 truncate text-sm font-semibold",
                      props.classNames?.title,
                    )}
                  >
                    {props.title}
                  </div>
                ) : null}
                {!resolvedCollapsed &&
                hasRenderableNode(props.headerActions) ? (
                  <div
                    className={cn(
                      "flex shrink-0 items-center gap-1",
                      props.classNames?.headerActions,
                    )}
                  >
                    {props.headerActions}
                  </div>
                ) : null}
                {collapsePlacement === "header" ? collapseButton() : null}
              </div>
            ) : null}

            {!resolvedCollapsed && props.search ? (
              <div
                className={cn(
                  "px-[var(--monkeys-component-navigation-sidebar-content-inline-padding,var(--global-spacing,0.75rem))] pb-2 pt-3",
                  appearance.unified && "pb-1",
                  props.classNames?.search,
                )}
              >
                <div
                  className={cn(
                    "flex items-center gap-2 rounded-md border border-input bg-[var(--monkeys-component-navigation-sidebar-search-background,hsl(var(--monkeys-color-control-surface)))] px-3 text-muted-foreground ring-offset-control-surface focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
                    appearance.searchHeight,
                    appearance.unified &&
                      "rounded-[var(--radius)] border-border bg-[var(--monkeys-component-navigation-sidebar-search-background,transparent)] px-2",
                  )}
                >
                  <span
                    className={cn(
                      "flex shrink-0",
                      appearance.unified && unifiedIcon,
                      props.classNames?.searchIcon,
                    )}
                    aria-hidden="true"
                  >
                    {props.search.icon ?? <SearchIcon />}
                  </span>
                  <input
                    type="search"
                    value={resolvedSearchValue}
                    placeholder={props.search.placeholder}
                    aria-label={props.search.ariaLabel}
                    className={cn(
                      "min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground",
                      props.classNames?.searchInput,
                    )}
                    onChange={(event) => {
                      const nextValue = event.currentTarget.value;
                      if (props.search?.value === undefined)
                        setInternalSearchValue(nextValue);
                      props.search?.onValueChange?.(nextValue);
                    }}
                  />
                  {resolvedSearchValue && props.search.clearLabel ? (
                    <button
                      type="button"
                      className={cn(
                        "inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        props.classNames?.searchClear,
                      )}
                      aria-label={props.search.clearLabel}
                      title={props.search.clearLabel}
                      onClick={() => {
                        if (props.search?.value === undefined)
                          setInternalSearchValue("");
                        props.search?.onValueChange?.("");
                      }}
                    >
                      <ClearIcon />
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div
              className={cn(
                "min-h-0 flex-1 overflow-y-auto px-[var(--monkeys-component-navigation-sidebar-content-inline-padding,var(--global-spacing,0.75rem))] py-3",
                appearance.unified && props.search && "pt-1",
                resolvedCollapsed && "px-1.5",
                props.classNames?.content,
              )}
            >
              {renderContent()}
            </div>

            {hasRenderableNode(props.footer) ||
            collapsePlacement === "footer" ? (
              <div
                className={cn(
                  "flex shrink-0 items-center gap-2 border-t border-border/70 p-3",
                  appearance.unified && "border-border",
                  resolvedCollapsed && "justify-center px-1",
                  props.classNames?.footer,
                )}
              >
                {!resolvedCollapsed && hasRenderableNode(props.footer) ? (
                  <div className="min-w-0 flex-1">{props.footer}</div>
                ) : null}
                {collapsePlacement === "footer" ? collapseButton() : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </aside>
      {props.kind !== "tree" ? (
        <MobileNavigation props={props} appearance={appearance} />
      ) : null}
    </>
  );
}

export type NavigationSidebarComponent = <
  TNodeSource = unknown,
  TGroupSource = unknown,
  TContext = unknown,
>(
  props: NavigationSidebarProps<TNodeSource, TGroupSource, TContext> &
    RefAttributes<HTMLElement>,
) => ReactElement | null;

export const NavigationSidebar = forwardRef(
  NavigationSidebarInner,
) as NavigationSidebarComponent;
